// Luxe — the editorial design language of the /client portal.
//
// Fashion-magazine feel: ivory paper, ink-black type, a single champagne-gold
// accent, a serif display face over a light sans, hairline rules, letter-spaced
// small caps. Every /client page imports its tokens from here (L) and mounts
// <LuxeStyles /> once (ClientLayout does it) for fonts, keyframes and the
// shared `lx-*` utility classes.
import React, { useEffect, useRef, useState } from 'react';

export const L = {
  // surfaces
  paper:     '#F6F3EE',   // page background — warm ivory
  paper2:    '#EFEAE2',   // recessed panels
  card:      '#FFFDF9',   // raised surfaces
  ink:       '#111111',   // primary type / black
  ink2:      '#3A3835',   // secondary type
  mute:      '#8C867D',   // captions, meta
  line:      'rgba(17,17,17,.12)',
  lineSoft:  'rgba(17,17,17,.06)',
  gold:      '#B8975A',
  goldSoft:  '#D9C49A',
  goldDeep:  '#8A6D3B',
  danger:    '#9E2B25',
  online:    '#3F7D5A',

  // type
  serif: "'Cormorant Garamond', 'Noto Serif SC', 'Songti SC', Georgia, serif",
  sans:  "'Jost', 'Noto Sans SC', 'PingFang SC', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif",

  // motion
  ease:  'cubic-bezier(.22,.61,.36,1)',
  easeOut: 'cubic-bezier(.16,1,.3,1)',

  radius: 2,              // editorial = nearly square corners
  radiusLg: 4,
  shadow: '0 1px 2px rgba(17,17,17,.04), 0 12px 32px -12px rgba(17,17,17,.18)',
  shadowLg: '0 2px 4px rgba(17,17,17,.05), 0 30px 80px -24px rgba(17,17,17,.35)',
};

// Small caps label — "ISSUE 07 · PROFILE".
export const kicker = (extra = {}) => ({
  fontFamily: L.sans, fontSize: 12.5, fontWeight: 500, letterSpacing: '.2em',
  textTransform: 'uppercase', color: L.mute, ...extra,
});

// Big serif headline.
export const display = (size = 40, extra = {}) => ({
  fontFamily: L.serif, fontSize: size, fontWeight: 500, lineHeight: 1.05,
  letterSpacing: '-.01em', color: L.ink, margin: 0, ...extra,
});

// Buttons.
export const btn = {
  primary: {
    fontFamily: L.sans, fontSize: 13.5, fontWeight: 500, letterSpacing: '.22em',
    textTransform: 'uppercase', background: L.ink, color: L.paper, border: `1px solid ${L.ink}`,
    padding: '13px 26px', borderRadius: L.radius, cursor: 'pointer',
    transition: `all .35s ${L.ease}`,
  },
  ghost: {
    fontFamily: L.sans, fontSize: 13.5, fontWeight: 500, letterSpacing: '.22em',
    textTransform: 'uppercase', background: 'transparent', color: L.ink, border: `1px solid ${L.ink}`,
    padding: '13px 26px', borderRadius: L.radius, cursor: 'pointer',
    transition: `all .35s ${L.ease}`,
  },
  icon: {
    width: 40, height: 40, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    background: 'transparent', border: `1px solid ${L.line}`, borderRadius: '50%', color: L.ink,
    cursor: 'pointer', transition: `all .3s ${L.ease}`,
  },
};

// ─── Diamond mark ────────────────────────────────────────────────────────────
// The FINDU logo mark: a brilliant-cut diamond in fine ink line, its crown
// facets picked out in champagne gold.
export const DiamondMark = ({ size = 34, ink = L.ink, gold = L.gold, strokeWidth = 1.15, style }) => (
  <svg width={size} height={size * 0.875} viewBox="0 0 40 35" fill="none" aria-hidden="true"
    strokeLinejoin="round" strokeLinecap="round" style={{ display: 'block', flexShrink: 0, ...style }}>
    <path d="M11 4H29L36 12L20 31L4 12Z" stroke={ink} strokeWidth={strokeWidth} />
    <path d="M4 12H36" stroke={ink} strokeWidth={strokeWidth} />
    <path d="M11 4L15.5 12L20 4L24.5 12L29 4" stroke={gold} strokeWidth={strokeWidth} />
    <path d="M15.5 12L20 31L24.5 12" stroke={ink} strokeWidth={strokeWidth} />
  </svg>
);

// ─── Membership tiers ────────────────────────────────────────────────────────
// Keyed by client_membership_plans.id. The browse API may not send
// membership_plan_id yet; tierOf() then falls back to CLASSIC.
//   bg      card background      fg   type colour on it
//   accent  rules / monogram     foil the sheen that sweeps across the card
export const TIERS = {
  classic:  { key: 'classic',  en: 'Member',          zh: '会员',
              bg: 'linear-gradient(160deg,#FBF8F3 0%,#EFE8DD 100%)', fg: L.ink, sub: L.mute,
              accent: L.ink, foil: 'rgba(255,255,255,.55)', dark: false },
  1: { key: 'verified', en: 'Verified',        zh: '验证会员',
       bg: 'linear-gradient(160deg,#FFFFFF 0%,#ECEDEF 100%)', fg: L.ink, sub: '#7C8087',
       accent: '#9AA0A8', foil: 'rgba(255,255,255,.75)', dark: false },
  2: { key: 'vip-a',    en: 'VIP',             zh: 'VIP',
       bg: 'linear-gradient(160deg,#F6EBD6 0%,#E3CFA6 100%)', fg: '#2B2215', sub: '#7A6440',
       accent: '#9C7B3E', foil: 'rgba(255,248,230,.7)', dark: false },
  3: { key: 'vip-b',    en: 'VIP Rosé',        zh: 'VIP 玫瑰金',
       bg: 'linear-gradient(160deg,#F4DED6 0%,#D9A99A 100%)', fg: '#2E1A16', sub: '#7D5248',
       accent: '#A5675A', foil: 'rgba(255,236,230,.7)', dark: false },
  4: { key: 'vvip',     en: 'VVIP',            zh: 'VVIP',
       bg: 'linear-gradient(160deg,#1F3B32 0%,#0F2520 100%)', fg: '#F3EBDD', sub: '#B9C7BC',
       accent: '#C9A867', foil: 'rgba(225,200,140,.28)', dark: true },
  5: { key: 'platinum', en: 'Platinum VVIP',   zh: '白金 VVIP',
       bg: 'linear-gradient(160deg,#E9ECEF 0%,#B9C0C8 55%,#DDE1E5 100%)', fg: '#16191D', sub: '#555C66',
       accent: '#6E7783', foil: 'rgba(255,255,255,.8)', dark: false },
  7: { key: 'black',    en: 'Black Gold VVIP', zh: '黑金 VVIP',
       bg: 'linear-gradient(160deg,#1A1A1A 0%,#050505 100%)', fg: '#F1E6CF', sub: '#A89A7E',
       accent: '#D4AF6A', foil: 'rgba(212,175,106,.35)', dark: true },
};
export const tierOf = (profile) => {
  const id = profile && (profile.membership_plan_id ?? profile.plan_id);
  return TIERS[id] || TIERS.classic;
};

// ─── Reveal-on-scroll ────────────────────────────────────────────────────────
// const ref = useReveal(); <div ref={ref} className="lx-reveal">…  — fades/lifts
// the element in the first time it enters the viewport.
export const useReveal = (options) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { el && el.classList.add('lx-in'); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('lx-in'); io.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08, ...options });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
};

// Same, for a whole list: <Reveal as="article" delay={i*60}>…</Reveal>
export const Reveal = ({ as: Tag = 'div', delay = 0, className = '', style, children, ...rest }) => {
  const ref = useReveal();
  return (
    <Tag ref={ref} className={`lx-reveal ${className}`} style={{ transitionDelay: `${delay}ms`, ...style }} {...rest}>
      {children}
    </Tag>
  );
};

export const useIsDesktop = (bp = 900) => {
  const [d, setD] = useState(() => (typeof window !== 'undefined' ? window.innerWidth >= bp : false));
  useEffect(() => {
    const h = () => setD(window.innerWidth >= bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return d;
};

// ─── Global stylesheet ───────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Jost:wght@300;400;500;600&family=Noto+Serif+SC:wght@400;500;600&display=swap');

.lx-root { background:${L.paper}; color:${L.ink}; font-family:${L.sans}; font-weight:400;
  -webkit-font-smoothing:antialiased; -moz-osx-font-smoothing:grayscale; }
.lx-root ::selection { background:${L.ink}; color:${L.paper}; }

/* paper grain — a fixed, very faint noise layer over the whole portal */
.lx-grain::after { content:''; position:fixed; inset:0; pointer-events:none; z-index:1;
  opacity:.05; mix-blend-mode:multiply;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E"); }

/* entrance */
@keyframes lxFadeUp { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:none; } }
@keyframes lxFade   { from { opacity:0; } to { opacity:1; } }
@keyframes lxSlideInRight { from { transform:translateX(100%); } to { transform:none; } }
@keyframes lxSlideOutRight { from { transform:none; } to { transform:translateX(100%); } }
@keyframes lxKenBurns { 0% { transform:scale(1.02) translate(0,0); } 100% { transform:scale(1.12) translate(-1.5%,-1%); } }
@keyframes lxSheen { 0% { transform:translateX(-130%) skewX(-18deg); } 100% { transform:translateX(230%) skewX(-18deg); } }
@keyframes lxLine { from { transform:scaleX(0); } to { transform:scaleX(1); } }
@keyframes lxPulse { 0%,100% { opacity:.35; } 50% { opacity:1; } }
@keyframes lxSpin { to { transform:rotate(360deg); } }

.lx-fade-up { animation:lxFadeUp .9s ${L.easeOut} backwards; }
.lx-fade { animation:lxFade .8s ${L.ease} backwards; }
.lx-d1 { animation-delay:.08s } .lx-d2 { animation-delay:.16s } .lx-d3 { animation-delay:.24s }
.lx-d4 { animation-delay:.32s } .lx-d5 { animation-delay:.40s } .lx-d6 { animation-delay:.48s }

.lx-reveal { opacity:0; transform:translateY(26px); transition:opacity 1s ${L.easeOut}, transform 1.1s ${L.easeOut}; }
.lx-reveal.lx-in { opacity:1; transform:none; }

.lx-kenburns { animation:lxKenBurns 14s ${L.ease} infinite alternate; will-change:transform; }

/* foil sheen sweeping across a card */
.lx-sheen { position:relative; overflow:hidden; }
.lx-sheen::before { content:''; position:absolute; top:-20%; bottom:-20%; left:0; width:38%; z-index:3; pointer-events:none;
  background:linear-gradient(90deg,transparent,var(--lx-foil,rgba(255,255,255,.5)),transparent);
  animation:lxSheen 5.5s ${L.ease} infinite; animation-delay:1.2s; }

/* image zoom on hover */
.lx-zoom { overflow:hidden; }
.lx-zoom img, .lx-zoom .lx-zoom-target { transition:transform 1.4s ${L.easeOut}, filter .8s ${L.ease}; }
.lx-zoom:hover img, .lx-zoom:hover .lx-zoom-target { transform:scale(1.06); }

/* underline that draws in */
.lx-link { position:relative; cursor:pointer; }
.lx-link::after { content:''; position:absolute; left:0; right:0; bottom:-3px; height:1px; background:currentColor;
  transform:scaleX(0); transform-origin:right; transition:transform .5s ${L.easeOut}; }
.lx-link:hover::after, .lx-link.lx-active::after { transform:scaleX(1); transform-origin:left; }

.lx-btn:hover { background:${L.paper} !important; color:${L.ink} !important; }
.lx-btn-ghost:hover { background:${L.ink} !important; color:${L.paper} !important; }
.lx-icon-btn:hover { background:${L.ink} !important; color:${L.paper} !important; border-color:${L.ink} !important; }

.lx-rule { height:1px; background:${L.line}; border:0; margin:0; }
.lx-rule-draw { transform-origin:left; animation:lxLine 1.2s ${L.easeOut} backwards; }

.lx-input { width:100%; box-sizing:border-box; background:transparent; border:0; border-bottom:1px solid ${L.line};
  padding:12px 0; font-family:${L.sans}; font-size:15px; color:${L.ink}; outline:none; border-radius:0;
  transition:border-color .3s ${L.ease}; }
.lx-input:focus { border-bottom-color:${L.ink}; }
.lx-input::placeholder { color:${L.mute}; font-weight:300; }

.lx-chip { font-family:${L.sans}; font-size:13.5px; letter-spacing:.06em; padding:8px 14px; border:1px solid ${L.line};
  background:transparent; color:${L.ink2}; border-radius:999px; cursor:pointer; transition:all .3s ${L.ease}; }
.lx-chip:hover { border-color:${L.ink}; color:${L.ink}; }
.lx-chip.lx-on { background:${L.ink}; border-color:${L.ink}; color:${L.paper}; }

.lx-noscroll::-webkit-scrollbar { display:none; } .lx-noscroll { scrollbar-width:none; -ms-overflow-style:none; }
.lx-root ::-webkit-scrollbar { width:8px; height:8px; }
.lx-root ::-webkit-scrollbar-thumb { background:rgba(17,17,17,.18); border-radius:8px; }
.lx-root ::-webkit-scrollbar-track { background:transparent; }

.lx-skel { background:linear-gradient(90deg,${L.paper2} 0%,#F8F5F0 50%,${L.paper2} 100%); background-size:200% 100%;
  animation:lxSkel 1.6s ease-in-out infinite; }
@keyframes lxSkel { 0% { background-position:200% 0; } 100% { background-position:-200% 0; } }

@media (prefers-reduced-motion: reduce) {
  .lx-kenburns, .lx-sheen::before, .lx-fade-up, .lx-fade, .lx-skel { animation:none !important; }
  .lx-reveal { opacity:1; transform:none; transition:none; }
}
`;

export const LuxeStyles = () => <style data-luxe>{CSS}</style>;
